/**
 * Who is signed in, behind an avatar (task 201).
 *
 * The header used to spend a two-line identity block and a Sign out pill
 * on this, about 300px that the sections needed more. Now it is a
 * brand-blue disc with the caller's initials; the menu behind it carries
 * the email, the role and home organization from their platform record
 * (Story 22, Story 48; marked "scoped" when it covers named Clients rather
 * than all), Preferences
 * (Story 26), Settings as a roadmap entry, and Sign out.
 */

import { useCallback, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';

import type { Me } from '@bluesphere/api-client';

import { CurrentDot, MenuDivider, MenuPanel, SoonItem, menuItemClass } from './MenuPanel';

export function AccountMenu({
  email,
  me,
  roleLine,
  onSignOut,
  pathname,
}: {
  email: string | null | undefined;
  me: Me | null;
  /** "Admin · Acme MSP · scoped" (`lib/users` `roleLine`). */
  roleLine: string | null;
  onSignOut: () => void;
  pathname: string;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  const onPreferences = pathname === '/preferences' || pathname.startsWith('/preferences/');

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        title={email || undefined}
        onClick={() => setOpen((o) => !o)}
        className={
          'grid h-9 w-9 place-items-center rounded-full bg-bluesphere-secondary text-xs font-bold ' +
          'tracking-wide text-bluesphere-secondaryFg transition-shadow hover:ring-[3px] ' +
          'hover:ring-bluesphere-secondary/40 focus-visible:outline-hidden focus-visible:ring-2 ' +
          'focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-bluesphere-bg ' +
          (open ? 'ring-[3px] ring-bluesphere-secondary/40' : '')
        }
      >
        {initials(me?.display_name, email)}
      </button>

      <MenuPanel open={open} onClose={close} triggerRef={buttonRef} align="right" label="Account">
        <div className="grid gap-0.5 px-3 pb-2 pt-2.5">
          <div className="truncate text-sm font-semibold">{email || 'unknown user'}</div>
          {roleLine && <div className="text-xs text-bluesphere-muted">{roleLine}</div>}
        </div>
        <MenuDivider />
        <Link to="/preferences" role="menuitem" className={menuItemClass}>
          Preferences
          {onPreferences && <CurrentDot />}
        </Link>
        <SoonItem label="Settings" />
        <MenuDivider />
        <button type="button" role="menuitem" className={menuItemClass} onClick={onSignOut}>
          Sign out
        </button>
      </MenuPanel>
    </div>
  );
}

/** Two letters: from the display name when there is one, else the email's local part. */
export function initials(
  displayName: string | undefined,
  email: string | null | undefined,
): string {
  const name = displayName?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
    return (first + last).toUpperCase();
  }
  const local = (email ?? '').split('@')[0] ?? '';
  const bits = local.split(/[._-]+/).filter(Boolean);
  if (bits.length >= 2) return ((bits[0]?.[0] ?? '') + (bits[1]?.[0] ?? '')).toUpperCase();
  return local.slice(0, 2).toUpperCase() || '?';
}
